import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { Box, Button, Card, Stack, Typography } from "@mui/material";
import {
    AccountBalanceWalletRounded,
    ArrowLeft,
    NotificationsActive,
    ReceiptLongRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { transaction_increaseQuery } from "../../network/api/transactions.api";
import { toPriceWithComma } from "../../helper/utils";

const _DashAlerts = () => {
    const navigate = useNavigate();
    const [pendingInvoices, setPendingInvoices] = useState(null);
    const corporate = useSelector(({ corporate }) => corporate.corporate);

    const [notificationPermission, setNotificationPermission] = useState("granted");
    useEffect(() => {
        try {
            setNotificationPermission(Notification?.permission);
        } catch (e) {}
    }, []);

    useEffect(() => {
        getPendingInvoices();
    }, [corporate]);

    function getPendingInvoices() {
        if (!corporate?.Id) return;
        transaction_increaseQuery({
            queryType: "FILTER",
            CorporateId: corporate.Id,
            DepositStatus: "DRAFT",
            paging: {
                Page: 0,
                Size: 10,
                Desc: true
            }
        })
            .then((result) => {
                setPendingInvoices(result?.data?.Data || null);
            })
            .catch(() => {
                setPendingInvoices({ content: [], totalElements: 0 });
            });
    }

    function requestForPermission(e) {
        e.preventDefault();
        try {
            Notification?.requestPermission().then(result => {
                setNotificationPermission(result);
                if (result === "granted") {
                    window.location = "/";
                }
            });
        } catch (e) {}
    }

    const [dismissNotice, setDismissNotice] = useState(false);

    const invoiceCount = pendingInvoices?.totalElements || pendingInvoices?.content?.length || 0;
    const invoiceList = pendingInvoices?.content || [];

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            {/* اعلان پیش‌فاکتورهای در انتظار پرداخت سازمان */}
            {pendingInvoices && invoiceCount > 0 && (
                <Card
                    sx={{
                        p: 2.2,
                        borderRadius: 3.5,
                        border: '1px solid',
                        borderColor: 'warning.light',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.06)',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        justifyContent: 'space-between',
                        gap: 2,
                        boxShadow: '0 2px 14px rgba(245, 158, 11, 0.08)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        <Box
                            sx={{
                                width: 46,
                                height: 46,
                                borderRadius: 2.5,
                                bgcolor: 'rgba(245, 158, 11, 0.18)',
                                color: 'warning.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <ReceiptLongRounded sx={{ fontSize: 26 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.3 }}>
                                شما {invoiceCount.toLocaleString('fa-IR')} پیش‌فاکتور در انتظار پرداخت دارید
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.8, alignItems: 'center' }}>
                                {invoiceList.slice(0, 3).map((item, idx) => {
                                    const serialShort = item.Serial?.Serial ? item.Serial.Serial.split("-")[0] : item.Serial;
                                    return (
                                        <Button
                                            key={item.Id || idx}
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            endIcon={<ArrowLeft sx={{ ml: -0.5 }} />}
                                            onClick={() => navigate("/finance/wallet")}
                                            sx={{
                                                borderRadius: 2,
                                                py: 0.3,
                                                px: 1.2,
                                                fontSize: '0.78rem',
                                                fontWeight: 700,
                                            }}
                                        >
                                            مبلغ: {toPriceWithComma(item.Amount)} تومان {serialShort ? `(شناسه ${serialShort})` : ''}
                                        </Button>
                                    );
                                })}
                                {invoiceCount > 3 && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                                        و {(invoiceCount - 3).toLocaleString('fa-IR')} مورد بیشتر...
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => navigate("/finance/wallet")}
                        startIcon={<AccountBalanceWalletRounded sx={{ ml: 0.5 }} />}
                        sx={{
                            borderRadius: 2.5,
                            px: 2.5,
                            py: 1,
                            fontWeight: 800,
                            whiteSpace: 'nowrap',
                            alignSelf: { xs: 'stretch', md: 'center' },
                            boxShadow: '0 4px 14px rgba(245, 158, 11, 0.25)'
                        }}
                    >
                        کیف‌پول و شارژ حساب سازمان
                    </Button>
                </Card>
            )}

            {/* بنر فعال‌سازی اعلان‌ها */}
            {notificationPermission !== "granted" && !dismissNotice && (
                <Card
                    sx={{
                        p: 2,
                        borderRadius: 3.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 2.5,
                                bgcolor: 'rgba(28, 139, 156, 0.1)',
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <NotificationsActive sx={{ fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                با فعال‌سازی اعلان‌ها، از تغییرات شارژ، وضعیت تیکت‌ها و درخواست‌های پرسنل مطلع شوید.
                            </Typography>
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}>
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={(e) => requestForPermission(e)}
                            sx={{ borderRadius: 2, px: 2.2, fontWeight: 700 }}
                        >
                            فعال‌سازی
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            color="inherit"
                            onClick={() => setDismissNotice(true)}
                            sx={{ borderRadius: 2, color: 'text.secondary', fontWeight: 600 }}
                        >
                            بعداً
                        </Button>
                    </Stack>
                </Card>
            )}
        </Stack>
    );
};

export default _DashAlerts;
